import useOTP from "@base/modules/user/account/hooks/useOTP";
import CommonStyles from "@components/CommonStyles";
import { Popconfirm } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import CaptchaWidget from "../CaptchaWidget";
import { useDispatch } from "react-redux";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";

const COUNTDOWN_SECONDS = 119;

export default function ExtraOTP() {
  //! state
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { handleGetOTP } = useOTP();
  const dispatch = useDispatch();

  const phoneUrl = searchParams.get("phone");
  const [timeCounter, setTimeCounter] = useState<number>(COUNTDOWN_SECONDS);
  const [isCaptchaOpen, setCaptchaOpen] = useState(false);

  //! function
  const funcTime = () => {
    const minutes = Math.floor(timeCounter / 60);
    const extraSeconds = timeCounter % 60;
    return `${String(minutes).padStart(2, "0")}:${String(extraSeconds).padStart(2, "0")} `;
  };

  const createInterval = useCallback(() => {
    clearInterval(intervalRef.current);
    setTimeCounter(COUNTDOWN_SECONDS);

    intervalRef.current = setInterval(() => {
      setTimeCounter((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    createInterval();

    return () => clearInterval(intervalRef.current);
  }, [createInterval]);

  const handleResendOTP = () => {
    if (!phoneUrl) {
      return;
    }

    setCaptchaOpen(true);
  };

  const handleCaptchaVerified = (turnstileToken: string) => {
    setCaptchaOpen(false);
    resendThenRestartCountdown(turnstileToken);
  };

  // A refused resend leaves the link available rather than starting a countdown for nothing.
  const resendThenRestartCountdown = async (turnstileToken: string) => {
    if (!phoneUrl) {
      return;
    }

    if (await handleGetOTP(phoneUrl, i18n.resolvedLanguage ?? "", turnstileToken)) {
      dispatch(setAlertMessage({ type: "success", message: t("Notification"), description: t("LoginPage.OtpResent") }));
      createInterval();
    }
  };

  //! render
  return (
    <div className="text-center mt-4">
      {timeCounter === 0 ? (
        <Popconfirm
          open={isCaptchaOpen}
          onOpenChange={(open) => setCaptchaOpen(open)}
          icon={null}
          title={t("LoginPage.CaptchaRequired")}
          description={isCaptchaOpen ? <CaptchaWidget onVerified={handleCaptchaVerified} /> : null}
          onCancel={() => setCaptchaOpen(false)}
          cancelText={t("Cancel")}
          okButtonProps={{ style: { display: "none" } }}
        >
          <span>
            <CommonStyles.TypographyUI className="font-semibold text-common-primary cursor-pointer" onClick={handleResendOTP}>
              {t("LoginPage.Resend code")}
            </CommonStyles.TypographyUI>
          </span>
        </Popconfirm>
      ) : (
        <CommonStyles.TypographyUI className="text-common-subtext">
          {t("LoginPage.TimeAuth")} (<span>{funcTime()}</span>)
        </CommonStyles.TypographyUI>
      )}
    </div>
  );
}
